import * as Sentry from "@sentry/react";
import { message } from "antd";
import AuthUtils from "utils/auth-utils";
import { ACCESS_TOKEN_KEY } from "constants/auth";
import {
  isSessionTerminating,
  markSessionTerminating
} from "services/session-state";
import { SentryTags, SentryErrorSources } from "../constants";

/**
 * Why a session ended. "user_initiated", "maintenance" and "inactivity" are
 * deliberate; every other reason means the user lost their session
 * unexpectedly and is worth a Sentry signal.
 */
export type SessionEndReason =
  | "user_initiated"
  | "maintenance"
  | "inactivity"
  | "refresh_failed"
  | "exchange_failed"
  | "auth_rejected";

const EXPECTED_REASONS: readonly SessionEndReason[] = [
  "user_initiated",
  "maintenance",
  "inactivity"
];

/**
 * Single exit point for the session — the teardown counterpart to the
 * token-refresh coordinator. Routing every logout through here (instead of
 * scattered direct logout() calls) is what lets us distinguish "user clicked
 * Logout" from "we ejected the user", and alert on the latter.
 *
 * The Sentry event fires BEFORE teardown: logout clears the token, and with
 * it the user identity the event needs. Symptom noise from the teardown
 * window (canceled requests, post-logout 401s) is suppressed in ErrorManager,
 * so this event is the one signal that a forced logout occurred.
 */
/**
 * logout() hard-redirects ~200ms in, which would wipe any toast before a
 * returning user can read it — so the inactivity path holds the teardown
 * long enough for the message to register.
 */
const INACTIVITY_TOAST_HOLD_MS = 2_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Machine-readable cause for a forced logout. Travels as a Sentry TAG, not
 * `extra` — free-text extras get server-side scrubbed to "[Filtered]", which
 * made the refresh_failed events undiagnosable.
 */
export type SessionEndDetailCode =
  | "missing_credentials"
  | "rejected_400"
  | "rejected_401"
  | "rejected_403"
  | "exhausted_transient"
  | "offline_grace_expired"
  | "heartbeat_401"
  | "unknown";

export async function endSession(
  reason: SessionEndReason,
  detail?: unknown,
  detailCode?: SessionEndDetailCode
): Promise<void> {
  if (reason === "inactivity") {
    // Flag teardown first so pollers stop and new requests cancel while the
    // toast is on screen, then let the user read WHY they are leaving.
    markSessionTerminating();
    message.error(
      "Your session ended due to inactivity. Please log in again.",
      5
    );
    await sleep(INACTIVITY_TOAST_HOLD_MS);
  }
  if (!EXPECTED_REASONS.includes(reason)) {
    Sentry.captureMessage(`Forced logout: ${reason}`, {
      level: "warning",
      tags: {
        [SentryTags.error_source]: SentryErrorSources.auth,
        [SentryTags.user_id]: AuthUtils.getLoggedinUserId() || "unknown",
        [SentryTags.logout_reason]: reason,
        [SentryTags.logout_detail]: detailCode || "unknown"
      },
      extra: {
        detail: detail instanceof Error ? detail.message : detail,
        path: window.location.pathname
      }
    });
  }
  await AuthUtils.logout();
}

export const DEFAULT_INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Shared across tabs via localStorage so activity in ANY tab keeps the whole
 * session alive — one idle background tab must not eject a user who is
 * actively working in another.
 */
const LAST_ACTIVITY_KEY = "ciq_last_activity";

// mousemove fires continuously; one storage write per interval is plenty.
const ACTIVITY_WRITE_THROTTLE_MS = 10_000;
const INACTIVITY_CHECK_INTERVAL_MS = 60_000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "mousemove"
] as const;

/**
 * Idle-session policy: the session ends only when EVERY tab has gone
 * `timeoutMs` (default 60 min) without user interaction — input in any one
 * tab stamps the shared timestamp and keeps all tabs alive.
 *
 * The decision is date-based (now vs the shared activity timestamp), never
 * trusted to a timer alone: browsers throttle background timers and freeze
 * them through sleep — the root cause of the original logout bug — so the
 * check also runs on tab wake. Wake CHECKS before it stamps: returning after
 * two idle hours must end the session, not refresh it.
 *
 * Also listens for the access token vanishing from another tab (storage
 * event) and tears this tab down locally — the departing tab already called
 * the backend logout.
 *
 * Returns a disposer that removes all listeners and the check interval.
 */
export function installInactivityLogout(
  options: { timeoutMs?: number } = {}
): () => void {
  const { timeoutMs = DEFAULT_INACTIVITY_TIMEOUT_MS } = options;
  let lastWrite = 0;
  let fired = false;

  const recordActivity = () => {
    const now = Date.now();
    if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }
    lastWrite = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  };

  const check = () => {
    if (fired || isSessionTerminating() || !AuthUtils.getAccessToken()) {
      return;
    }
    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    if (lastActivity && Date.now() - lastActivity >= timeoutMs) {
      fired = true;
      void endSession("inactivity");
    }
  };

  const onWake = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    check();
    if (!fired) {
      recordActivity();
    }
  };

  const onStorage = (event: StorageEvent) => {
    // Another tab removed the token (it logged out) — leave quietly without a
    // second backend logout call.
    if (event.key === ACCESS_TOKEN_KEY && event.newValue === null) {
      markSessionTerminating();
      window.location.href = "/";
    }
  };

  // Opening/refreshing a tab counts as activity — never start "already idle".
  recordActivity();

  ACTIVITY_EVENTS.forEach((eventName) =>
    window.addEventListener(eventName, recordActivity, { passive: true })
  );
  document.addEventListener("visibilitychange", onWake);
  window.addEventListener("focus", onWake);
  window.addEventListener("storage", onStorage);
  const intervalId = setInterval(check, INACTIVITY_CHECK_INTERVAL_MS);

  return () => {
    ACTIVITY_EVENTS.forEach((eventName) =>
      window.removeEventListener(eventName, recordActivity)
    );
    document.removeEventListener("visibilitychange", onWake);
    window.removeEventListener("focus", onWake);
    window.removeEventListener("storage", onStorage);
    clearInterval(intervalId);
  };
}
